import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useMutation, useQuery } from '@tanstack/react-query'
import Papa from 'papaparse'
import { api } from '../utils/api'
import AssetTemplateBuilder from '../components/AssetTemplateBuilder'
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Download, ChevronRight, Table2, Monitor, File, Server, Settings2, ChevronDown, Loader2, RefreshCw, Check, Search, Link2 } from 'lucide-react'

interface CompanyItem { id: string; name: string }

// ─── CSV Import ───────────────────────────────────────────────────────────────

const FIELD_MAP: Record<string, string> = {
  'รหัสทรัพย์สิน': 'assetTag', 'asset tag': 'assetTag', 'assettag': 'assetTag',
  'เลขเครื่อง': 'assetCode', 'asset code': 'assetCode', 'assetcode': 'assetCode', 'asset_code': 'assetCode',
  'ชื่อ': 'name', 'name': 'name', 'asset name': 'name',
  'ประเภท': 'type', 'type': 'type',
  'หมวดหมู่': 'category', 'category': 'category',
  'serial number': 'serialNumber', 'serial': 'serialNumber', 'sn': 'serialNumber', 's/n': 'serialNumber',
  'ยี่ห้อ': 'brand', 'brand': 'brand',
  'model': 'model', 'รุ่น': 'model',
  'สเปค': 'spec', 'spec': 'spec',
  'สถานะ': 'status', 'status': 'status',
  'บริษัท': 'company', 'company': 'company',
  'อาคาร': 'building', 'building': 'building',
  'ชั้น': 'floor', 'floor': 'floor',
  'ห้อง': 'room', 'room': 'room', 'ที่ตั้ง': 'room',
  'รหัสพนักงาน': 'employeeId', 'employee id': 'employeeId', 'emp id': 'employeeId',
  'ผู้ใช้': 'assignedTo', 'assigned to': 'assignedTo', 'ผู้ใช้งาน': 'assignedTo',
  'หน่วยงาน': 'department', 'department': 'department', 'แผนก': 'department',
  'ip': 'ipAddress', 'ip address': 'ipAddress',
  'ethernet mac': 'macEthernet', 'mac': 'macEthernet',
  'wifi mac': 'macWifi',
  'bluetooth mac': 'macBluetooth',
  'ราคา': 'purchasePrice', 'purchase price': 'purchasePrice', 'ราคาซื้อ': 'purchasePrice',
  'วันที่ซื้อ': 'purchaseDate', 'purchase date': 'purchaseDate',
  'วันหมดประกัน': 'warrantyExpiry', 'warranty': 'warrantyExpiry',
  'หมดสัญญา': 'contractEnd',
  'ค่าเสื่อม': 'depreciation', 'depreciation': 'depreciation',
  'net book': 'netBook', 'net book value': 'netBook',
  'cost center': 'costCenter', 'ศูนย์ต้นทุน': 'costCenter',
  'หมายเหตุ': 'notes', 'notes': 'notes', 'remark': 'notes',
  'license': 'licenseInfo', 'licenseinfo': 'licenseInfo',
}

const TYPE_MAP: Record<string, string> = {
  'laptop': 'LAPTOP', 'โน้ตบุ๊ค': 'LAPTOP', 'โน้ตบุก': 'LAPTOP', 'notebook': 'LAPTOP',
  'monitor': 'MONITOR', 'จอ': 'MONITOR', 'display': 'MONITOR',
  'phone': 'PHONE', 'มือถือ': 'PHONE', 'smartphone': 'PHONE',
  'printer': 'PRINTER', 'เครื่องพิมพ์': 'PRINTER',
  'network': 'NETWORK', 'switch': 'NETWORK', 'router': 'NETWORK', 'เครือข่าย': 'NETWORK',
  'other': 'OTHER', 'อื่นๆ': 'OTHER',
}
const STATUS_MAP: Record<string, string> = {
  'in use': 'IN_USE', 'ใช้งาน': 'IN_USE', 'in_use': 'IN_USE', 'inuse': 'IN_USE',
  'available': 'AVAILABLE', 'ว่าง': 'AVAILABLE', 'พร้อมใช้': 'AVAILABLE',
  'maintenance': 'MAINTENANCE', 'ซ่อม': 'MAINTENANCE',
  'retired': 'RETIRED', 'เลิกใช้': 'RETIRED',
  'lost': 'LOST', 'สูญหาย': 'LOST',
}

interface ParsedRow { [key: string]: string }
interface MappedRow { [key: string]: any; _rowIndex: number; _errors: string[] }
type MasterOption = { label: string; value: string }
interface KnownOptions { types: MasterOption[]; statuses: MasterOption[] }

// Accept the master-data value or label as well as the built-in aliases.
function matchOption(val: string, aliases: Record<string, string>, opts: MasterOption[], fallback: string) {
  const lower = val.toLowerCase()
  return aliases[lower] || opts.find(o => o.value.toLowerCase() === lower || o.label.toLowerCase() === lower)?.value || fallback
}

function mapRow(raw: ParsedRow, rowIdx: number, known: KnownOptions = { types: [], statuses: [] }): MappedRow {
  const mapped: MappedRow = { _rowIndex: rowIdx, _errors: [] }
  for (const [rawKey, rawVal] of Object.entries(raw)) {
    const normalKey = rawKey.toLowerCase().trim()
    const fieldName = FIELD_MAP[normalKey]
    if (!fieldName || !rawVal?.trim()) continue
    let val: any = rawVal.trim()
    if (fieldName === 'type') val = matchOption(val, TYPE_MAP, known.types, 'OTHER')
    if (fieldName === 'status') val = matchOption(val, STATUS_MAP, known.statuses, 'AVAILABLE')
    if (['purchasePrice','netBook','depreciation','rentPrice','currentValue'].includes(fieldName))
      val = parseFloat(val.replace(/[,฿]/g, '')) || null
    if (['purchaseDate','warrantyExpiry','contractEnd','maDate'].includes(fieldName)) {
      const num = Number(val)
      if (!isNaN(num) && Number.isInteger(num) && num > 1000) {
        val = new Date((num - 25569) * 86400000).toISOString()
      } else if (val) {
        val = new Date(val).toISOString()
      } else {
        val = null
      }
    }
    mapped[fieldName] = val
  }
  if (!mapped.name && mapped.assetCode) mapped.name = mapped.assetCode
  if (!mapped.assetCode) mapped._errors.push('ไม่มี ASSET_CODE')
  return mapped
}

// Preview columns, in display order — only fields that appear in at least one row are shown
const PREVIEW_FIELDS: [string, string][] = [
  ['assetCode', 'Asset Code'], ['assetTag', 'Asset Tag'], ['name', 'ชื่อ'], ['type', 'ประเภท'], ['category', 'หมวดหมู่'],
  ['serialNumber', 'Serial'], ['brand', 'ยี่ห้อ'], ['model', 'Model'], ['spec', 'สเปค'], ['status', 'สถานะ Asset'],
  ['company', 'บริษัท (ในไฟล์)'], ['building', 'อาคาร'], ['floor', 'ชั้น'], ['room', 'ห้อง'], ['department', 'แผนก'],
  ['employeeId', 'รหัสพนักงาน'], ['assignedTo', 'ผู้ใช้'], ['ipAddress', 'IP'], ['macEthernet', 'Ethernet MAC'],
  ['macWifi', 'WiFi MAC'], ['macBluetooth', 'Bluetooth MAC'], ['purchasePrice', 'ราคา'], ['purchaseDate', 'วันที่ซื้อ'],
  ['warrantyExpiry', 'วันหมดประกัน'], ['contractEnd', 'หมดสัญญา'], ['depreciation', 'ค่าเสื่อม'], ['netBook', 'Net Book'],
  ['costCenter', 'Cost Center'], ['licenseInfo', 'License'], ['notes', 'หมายเหตุ'],
]
const MONO_FIELDS = new Set(['assetCode', 'assetTag', 'serialNumber', 'ipAddress', 'macEthernet', 'macWifi', 'macBluetooth'])

const TEMPLATE_HEADERS = ['ASSET_CODE','ประเภท','หมวดหมู่','serial number','ยี่ห้อ','model','สเปค','สถานะ','รหัสพนักงาน','ผู้ใช้','ราคา','วันที่ซื้อ']
const TEMPLATE_EXAMPLE = ['IL-NB-0001','LAPTOP','Hardware','PF2T9D9L','Lenovo','ThinkPad L14 Gen1','AMD Ryzen 5 4500U','IN_USE','IL67001','korakots','23500','2022-07-23']

// ─── WinAudit Parser ──────────────────────────────────────────────────────────

interface WinAuditData {
  name?: string; brand?: string; model?: string; serialNumber?: string
  os?: string; cpu?: string; ram?: string; hdd?: string
  ipAddress?: string; macEthernet?: string; macWifi?: string
  type?: string; assetCode?: string; status?: string
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let inQuote = false
  let current = ''
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuote && line[i + 1] === '"') { current += '"'; i++ }
      else { inQuote = !inQuote }
    } else if (ch === ',' && !inQuote) {
      result.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  result.push(current.trim())
  return result
}

function normalizeMAC(mac: string): string {
  return mac.replace(/-/g, ':').toUpperCase()
}

function applyWinAuditField(result: WinAuditData, section: string, item: string, rawValue: string, netTypeRef: { current: string }) {
  if (!rawValue?.trim()) return
  const value = rawValue.trim()

  // Computer Name → assetCode
  if (item === 'computer name' || (item === 'name' && (section.includes('computer') || section === '')))
    result.assetCode = result.assetCode || value
  if (item === 'manufacturer' || (item.includes('manufacturer') && (section.includes('computer') || section === '')))
    result.brand = result.brand || value
  if (item === 'model' && !section.includes('network') && !section.includes('disk') && !section.includes('drive'))
    result.model = result.model || value
  if (item === 'serial number' && !section.includes('network'))
    result.serialNumber = result.serialNumber || value
  if (item === 'operating system' || (section.includes('operating system') && (item === 'name' || item === 'caption')))
    result.os = result.os || value.replace(/^Microsoft\s+/, '')
  if (item === 'processor description' || item === 'processor name' || (section.includes('processor') && (item === 'name' || item === 'caption' || item === 'description')))
    result.cpu = result.cpu || value
  if (item === 'total memory')
    result.ram = result.ram || value
  if (item === 'total hard drive')
    result.hdd = result.hdd || value
  if (section.includes('network') || section.includes('adapter') || section.includes('nic')) {
    if (item === 'name' || item === 'description' || item === 'connection name' || item === 'device name') {
      const lower = value.toLowerCase()
      netTypeRef.current = (lower.includes('wi-fi') || lower.includes('wifi') || lower.includes('wireless')) ? 'wifi' : 'ethernet'
    }
    if (item === 'ip address' || item === 'ipv4 address') {
      if (!value.startsWith('169.') && value !== '0.0.0.0' && !value.includes(':'))
        result.ipAddress = result.ipAddress || value
    }
    if (item === 'mac address' || item === 'physical address') {
      const mac = normalizeMAC(value)
      if (/^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(mac)) {
        if (netTypeRef.current === 'wifi') result.macWifi = result.macWifi || mac
        else result.macEthernet = result.macEthernet || mac
      }
    }
  }
}

function parseWinAuditCSV(content: string): WinAuditData {
  const lines = content.split('\n').map(l => l.trim()).filter(Boolean)
  const result: WinAuditData = {}
  const netTypeRef = { current: 'ethernet' }
  if (!lines.length) return result

  // Real-world WinAudit CSV export uses a fixed table layout:
  // ItemOrder,RecordNumber,CategoryID,CategoryName,ItemID,ItemName,ItemValue
  // — section/item/value live in columns 3/5/6, not 0/1/2, so detect that
  // header explicitly instead of assuming a plain Section,Item,Value shape.
  const header = parseCSVLine(lines[0]).map(h => h.toLowerCase())
  const idxCategory = header.indexOf('categoryname')
  const idxItem = header.indexOf('itemname')
  const idxValue = header.indexOf('itemvalue')
  const isTableFormat = idxCategory !== -1 && idxItem !== -1 && idxValue !== -1

  if (isTableFormat) {
    for (let i = 1; i < lines.length; i++) {
      const cells = parseCSVLine(lines[i])
      if (cells.length <= Math.max(idxCategory, idxItem, idxValue)) continue
      const section = (cells[idxCategory] || '').toLowerCase()
      const item = (cells[idxItem] || '').toLowerCase()
      const value = cells[idxValue] || ''
      applyWinAuditField(result, section, item, value, netTypeRef)
    }
  } else {
    for (const line of lines) {
      const cells = parseCSVLine(line)
      if (cells.length < 2) continue

      let section = '', item = '', value = ''
      if (cells.length >= 3) {
        section = cells[0].toLowerCase()
        item = cells[1].toLowerCase()
        value = cells[2]
      } else {
        item = cells[0].toLowerCase()
        value = cells[1]
      }
      applyWinAuditField(result, section, item, value, netTypeRef)
    }
  }

  const modelLower = (result.model || '').toLowerCase()
  if (modelLower.includes('notebook') || modelLower.includes('laptop') || modelLower.includes('thinkpad') || modelLower.includes('book'))
    result.type = 'LAPTOP'

  return result
}

function parseWinAuditHTML(content: string): WinAuditData {
  const parser = new DOMParser()
  const doc = parser.parseFromString(content, 'text/html')

  // Build flat map: item name (lowercase) → first value found across all tables
  const flat: Record<string, string> = {}
  doc.querySelectorAll('table tr').forEach(row => {
    const tds = Array.from(row.querySelectorAll('td'))
    if (tds.length < 2) return
    const key = tds[0].textContent?.trim() || ''
    const val = tds[1].textContent?.trim() || ''
    if (key && val && !(key.toLowerCase() in flat)) {
      flat[key.toLowerCase()] = val
    }
  })

  const get = (...keys: string[]) => keys.map(k => flat[k.toLowerCase()]).find(v => v && v.trim()) || undefined

  // Network: need ordered list to detect ethernet vs wifi
  let macEthernet: string | undefined
  let macWifi: string | undefined
  let ipAddress: string | undefined
  let currentIsWifi = false
  doc.querySelectorAll('table tr').forEach(row => {
    const tds = Array.from(row.querySelectorAll('td'))
    if (tds.length < 2) return
    const key = (tds[0].textContent?.trim() || '').toLowerCase()
    const val = tds[1].textContent?.trim() || ''
    if (!val) return
    if (key === 'connection name' || key === 'description') {
      currentIsWifi = val.toLowerCase().includes('wi-fi') || val.toLowerCase().includes('wifi') || val.toLowerCase().includes('wireless')
    }
    if (key === 'mac address' || key === 'physical address') {
      const mac = normalizeMAC(val)
      if (/^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(mac)) {
        if (currentIsWifi) { macWifi = macWifi || mac }
        else { macEthernet = macEthernet || mac }
      }
    }
    if ((key === 'ip address' || key === 'ipv4 address') && !val.startsWith('169.') && val !== '0.0.0.0' && !val.includes(':')) {
      ipAddress = ipAddress || val
    }
  })

  const result: WinAuditData = {
    assetCode:    get('computer name'),
    brand:        get('manufacturer'),
    model:        get('model'),
    serialNumber: get('serial number'),
    os:           (get('operating system') || '').replace(/^Microsoft\s+/, '') || undefined,
    cpu:          get('processor description', 'processor name'),
    ram:          get('total memory'),
    hdd:          get('total hard drive'),
    ipAddress,
    macEthernet,
    macWifi,
  }

  const modelLower = (result.model || '').toLowerCase()
  if (modelLower.includes('notebook') || modelLower.includes('laptop') || modelLower.includes('thinkpad') || modelLower.includes('book'))
    result.type = 'LAPTOP'

  return result
}

function parseWinAudit(file: File, content: string): WinAuditData {
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (ext === 'html' || ext === 'htm') return parseWinAuditHTML(content)
  return parseWinAuditCSV(content)
}

// ─── WinAudit Tab Component ───────────────────────────────────────────────────

const WINAUDIT_FIELDS: { key: keyof WinAuditData; label: string; placeholder?: string }[] = [
  { key: 'assetCode',    label: 'Asset Code * (Computer Name)', placeholder: 'ดึงจาก Computer Name อัตโนมัติ' },
  { key: 'brand',        label: 'ยี่ห้อ (Manufacturer)' },
  { key: 'model',        label: 'รุ่น (Model)' },
  { key: 'serialNumber', label: 'Serial Number' },
  { key: 'os',           label: 'ระบบปฏิบัติการ (Operating System)' },
  { key: 'cpu',          label: 'CPU (Processor Description)' },
  { key: 'ram',          label: 'RAM (Total Memory)' },
  { key: 'hdd',          label: 'Storage (Total Hard Drive)' },
  { key: 'ipAddress',    label: 'IP Address' },
  { key: 'macEthernet',  label: 'Ethernet MAC' },
  { key: 'macWifi',      label: 'WiFi MAC' },
]

function WinAuditImport({ companies }: { companies: CompanyItem[] }) {
  const navigate = useNavigate()
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload')
  const [winFile, setWinFile] = useState<File | null>(null)
  const [edited, setEdited] = useState<WinAuditData>({})
  const [selectedCompany, setSelectedCompany] = useState('')
  const [result, setResult] = useState<{ ok: boolean; assetId?: string; error?: string } | null>(null)

  const { data: assetTypes = [] } = useQuery<{ id: string; label: string; value: string }[]>({
    queryKey: ['masterdata', 'asset-types'],
    queryFn: () => api.get('/masterdata/asset-types').then(r => r.data).catch(() => []),
  })

  const handleFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      const data = parseWinAudit(file, content)
      setEdited(data)
      setWinFile(file)
      setStep('preview')
    }
    reader.readAsText(file, 'UTF-8')
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => files.length && handleFile(files[0]),
    accept: { 'text/csv': ['.csv'], 'text/html': ['.html', '.htm'], 'text/plain': ['.txt'] },
    maxFiles: 1,
  })

  const importMutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      const assetData: any = {
        ...edited,
        name: edited.assetCode || 'Unknown',
        type: edited.type || 'LAPTOP',
        status: edited.status || 'AVAILABLE',
        ...(selectedCompany && { companyId: selectedCompany }),
      }
      fd.append('data', JSON.stringify(assetData))
      if (winFile) fd.append('file', winFile)
      const res = await api.post('/assets/import/winaudit', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      return res.data
    },
    onSuccess: (data) => { setResult({ ok: true, assetId: data.id }); setStep('done') },
    onError: (e: any) => { setResult({ ok: false, error: e.response?.data?.message || e.message }); setStep('done') },
  })

  if (step === 'upload') return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <Monitor size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">วิธีใช้งาน WinAudit Import</p>
          <ul className="text-sm text-blue-700 mt-1 space-y-1 list-disc list-inside">
            <li>เปิด <strong>WinAudit Free</strong> บนเครื่องที่ต้องการ audit</li>
            <li>ไปที่ <strong>File → Save As</strong> → เลือกประเภท <strong>CSV</strong> หรือ <strong>HTML</strong></li>
            <li>อัปโหลดไฟล์ที่ได้ — ระบบจะแยกข้อมูล Hardware อัตโนมัติ</li>
            <li>ไฟล์ WinAudit จะถูกเก็บไว้กับ Asset ในระบบด้วย</li>
          </ul>
        </div>
      </div>
      <div {...getRootProps()} className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}>
        <input {...getInputProps()} />
        <Monitor size={32} className="mx-auto text-gray-300 mb-3" />
        <p className="font-medium text-gray-600">{isDragActive ? 'วางไฟล์ที่นี่...' : 'Drag & drop หรือคลิกเลือกไฟล์ WinAudit'}</p>
        <p className="text-sm text-gray-400 mt-1">รองรับ .csv .html .htm .txt จาก WinAudit</p>
      </div>
    </div>
  )

  if (step === 'preview') return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
        <File size={16} className="text-gray-500" />
        <span className="text-sm text-gray-700 font-medium">{winFile?.name}</span>
        <button onClick={() => setStep('upload')} className="ml-auto text-xs text-gray-400 hover:text-red-500">เปลี่ยนไฟล์</button>
      </div>
      <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-800">
        ระบบแยกข้อมูล Hardware ได้แล้ว — ตรวจสอบและแก้ไขก่อน Import
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {WINAUDIT_FIELDS.map(({ key, label, placeholder }) => (
          <div key={key}>
            <label className="block text-xs text-gray-500 mb-1">{label}</label>
            <input
              type="text"
              value={(edited as any)[key] || ''}
              onChange={e => setEdited(prev => ({ ...prev, [key]: e.target.value }))}
              placeholder={placeholder || 'ข้อมูลจาก WinAudit'}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ))}
        <div>
          <label className="block text-xs text-gray-500 mb-1">ประเภท Asset</label>
          <select value={edited.type || 'LAPTOP'} onChange={e => setEdited(prev => ({ ...prev, type: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            {assetTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">สถานะ</label>
          <select value={edited.status || 'AVAILABLE'} onChange={e => setEdited(prev => ({ ...prev, status: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="AVAILABLE">พร้อมใช้</option>
            <option value="IN_USE">กำลังใช้งาน</option>
            <option value="MAINTENANCE">ซ่อมบำรุง</option>
            <option value="RETIRED">เลิกใช้</option>
            <option value="LOST">สูญหาย</option>
          </select>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
        <label className="text-sm font-medium text-blue-800 whitespace-nowrap">บริษัท</label>
        <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">— ไม่ระบุ —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {!edited.assetCode && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <AlertTriangle size={15} />
          <span>กรุณากรอก Asset Code ก่อน Import</span>
        </div>
      )}

      <div className="flex gap-3">
        <button onClick={() => setStep('upload')} className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm hover:bg-gray-50">← กลับ</button>
        <button onClick={() => importMutation.mutate()} disabled={!edited.assetCode || importMutation.isPending}
          className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          {importMutation.isPending ? 'กำลัง Import...' : 'Import Asset จาก WinAudit'}
        </button>
      </div>
    </div>
  )

  if (step === 'done') return (
    <div className="text-center py-12 space-y-4">
      {result?.ok ? (
        <>
          <CheckCircle size={48} className="mx-auto text-green-500" />
          <h2 className="text-xl font-bold text-gray-900">Import สำเร็จ!</h2>
          <p className="text-sm text-gray-500">ไฟล์ WinAudit ถูกบันทึกไว้กับ Asset แล้ว</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { setStep('upload'); setWinFile(null); setEdited({}) }}
              className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm">Import ไฟล์อื่น</button>
            {result.assetId && <button onClick={() => navigate(`/assets/${result.assetId}`)}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">ดู Asset →</button>}
          </div>
        </>
      ) : (
        <>
          <XCircle size={48} className="mx-auto text-red-500" />
          <h2 className="text-xl font-bold text-gray-900">Import ไม่สำเร็จ</h2>
          <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{result?.error}</p>
          <button onClick={() => setStep('preview')} className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm">← กลับแก้ไข</button>
        </>
      )}
    </div>
  )

  return null
}

// ─── GLPI Sync Tab Component ───────────────────────────────────────────────────

interface GlpiConfigData { url: string; appToken: string; userToken: string; configured: boolean }

function GlpiConfigPanel({ onConfigured, onUrlChange }: { onConfigured: (configured: boolean) => void; onUrlChange: (url: string) => void }) {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [appToken, setAppToken] = useState('')
  const [userToken, setUserToken] = useState('')
  const [configured, setConfigured] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const load = () => {
    api.get<GlpiConfigData>('/glpi/config').then(r => {
      setUrl(r.data.url || '')
      setAppToken(r.data.appToken || '')
      setUserToken(r.data.userToken || '')
      setConfigured(r.data.configured)
      onConfigured(r.data.configured)
      onUrlChange(r.data.url || '')
    }).catch(() => {})
  }
  useEffect(load, [])

  const save = async () => {
    setSaving(true); setMsg(null)
    try {
      await api.put('/glpi/config', { url, appToken, userToken })
      setMsg({ ok: true, text: 'บันทึกเรียบร้อย' })
      load()
    } catch (e: any) {
      setMsg({ ok: false, text: e.response?.data?.message || 'บันทึกไม่สำเร็จ' })
    } finally { setSaving(false) }
  }
  const test = async () => {
    setTesting(true); setMsg(null)
    try {
      const r = await api.post('/glpi/config/test', { url, appToken, userToken })
      setMsg({ ok: true, text: r.data.message })
    } catch (e: any) {
      setMsg({ ok: false, text: e.response?.data?.message || 'เชื่อมต่อไม่สำเร็จ' })
    } finally { setTesting(false) }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50">
        <Settings2 size={15} className="text-gray-500" />
        <span className="text-sm font-medium text-gray-800">การเชื่อมต่อ GLPI Server</span>
        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${configured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {configured ? 'ตั้งค่าแล้ว' : 'ยังไม่ตั้งค่า'}
        </span>
        <ChevronDown size={15} className={`ml-auto text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">GLPI URL</label>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="http://10.1.2.81:8081"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-gray-400 mt-1">ระบบจะเรียก <code>{'{URL}'}/apirest.php</code> อัตโนมัติ</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">User Token (Personal Token)</label>
            <input value={userToken} onChange={e => setUserToken(e.target.value)} type="password"
              onFocus={e => { if (e.target.value === '••••••••') setUserToken('') }}
              placeholder="GLPI → Preferences → API tokens → Regenerate"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">App Token (ถ้ามี — ไม่บังคับ)</label>
            <input value={appToken} onChange={e => setAppToken(e.target.value)} type="password"
              onFocus={e => { if (e.target.value === '••••••••') setAppToken('') }}
              placeholder="GLPI → Setup → General → API"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          {msg && <div className={`text-xs rounded-lg px-3 py-2 ${msg.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>}
          <div className="flex gap-2">
            <button onClick={save} disabled={saving || !url || !userToken}
              className="flex items-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white px-3 py-2 rounded-lg">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} บันทึก
            </button>
            <button onClick={test} disabled={testing || !url || !userToken}
              className="flex items-center gap-1.5 text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg">
              {testing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} ทดสอบการเชื่อมต่อ
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const GLPI_AGENT_VERSION = '1.19'
const GLPI_AGENT_MSI_URL = `https://github.com/glpi-project/glpi-agent/releases/download/${GLPI_AGENT_VERSION}/GLPI-Agent-${GLPI_AGENT_VERSION}-x64.msi`

function AgentDownloadPanel({ glpiUrl }: { glpiUrl: string }) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const serverUrl = (glpiUrl || '{GLPI URL}').replace(/\/+$/, '') + '/front/inventory.php'
  // RUNNOW=1 asks the Windows service to scan immediately, but the service can take a
  // while to come up (or on some machines needs a manual kick) — so we chain a direct,
  // one-off forced run of the agent script right after install to guarantee the first
  // inventory lands in GLPI without waiting on the service.
  // Plain `msiexec ...` in PowerShell can return before the install actually finishes
  // (it hands off to a background installer process), so we use Start-Process -Wait
  // to block until the MSI is truly done before touching the newly-installed files.
  // The .msi is downloaded via the browser under whichever Windows account is logged
  // into the desktop, but PowerShell is often opened "as a different user" (a separate
  // admin account) — so $env:USERPROFILE\Downloads is frequently the WRONG folder.
  // We search every profile's Downloads (falling back to a full C:\Users search) to
  // find the file regardless of which account downloaded it.
  const agentExe = 'C:\\Program Files\\GLPI-Agent\\perl\\bin\\glpi-agent.exe'
  const agentScript = 'C:\\Program Files\\GLPI-Agent\\perl\\bin\\glpi-agent'
  const agentIncludes = ['agent', 'site\\lib', 'vendor\\lib', 'lib']
    .map(p => `-I"C:\\Program Files\\GLPI-Agent\\perl\\${p}"`).join(' ')
  const msiName = `GLPI-Agent-${GLPI_AGENT_VERSION}-x64.msi`
  const cmd = [
    `$msi = (Get-ChildItem "C:\\Users\\*\\Downloads\\${msiName}" -ErrorAction SilentlyContinue | Select-Object -First 1 -ExpandProperty FullName)`,
    `if (-not $msi) { $msi = (Get-ChildItem -Path C:\\Users -Recurse -Filter "${msiName}" -ErrorAction SilentlyContinue | Select-Object -First 1 -ExpandProperty FullName) }`,
    `if (-not $msi) { Write-Host "ไม่พบไฟล์ ${msiName} ในเครื่องนี้ — ดาวน์โหลดก่อน" -ForegroundColor Red } else { Start-Process msiexec.exe -ArgumentList ('/i "{0}" /quiet SERVER="${serverUrl}" RUNNOW=1 ADD_FIREWALL_EXCEPTION=1' -f $msi) -Wait; if (Test-Path "${agentExe}") { & "${agentExe}" ${agentIncludes} "${agentScript}" --server="${serverUrl}" --force } else { Write-Host "ติดตั้งไม่สำเร็จ — ไม่พบ ${agentExe}" -ForegroundColor Red } }`,
  ].join('; ')

  const copy = () => {
    navigator.clipboard?.writeText(cmd).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50">
        <Download size={15} className="text-gray-500" />
        <span className="text-sm font-medium text-gray-800">ดาวน์โหลด & ติดตั้ง GLPI Agent (เครื่องลูกค้า)</span>
        <ChevronDown size={15} className={`ml-auto text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100">
          <p className="text-sm text-gray-600">
            ติดตั้ง agent นี้บนเครื่อง Windows ของลูกค้า 1 ครั้ง — จะสแกน hardware/software แล้วส่งเข้า GLPI อัตโนมัติทุกวัน จากนั้นกลับมาที่หน้านี้เพื่อ sync เข้า Asset
          </p>
          <a href={GLPI_AGENT_MSI_URL} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium">
            <Download size={15} /> ดาวน์โหลด GLPI-Agent-{GLPI_AGENT_VERSION}-x64.msi
          </a>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">คำสั่งติดตั้ง + สแกนทันที (รันบน PowerShell แบบ Administrator บนเครื่องลูกค้า — วางทั้งก้อนแล้ว Enter)</label>
            <div className="flex items-start gap-2">
              <code className="flex-1 block text-xs bg-gray-900 text-green-400 rounded-lg px-3 py-2 overflow-x-auto whitespace-pre">{cmd}</code>
              <button onClick={copy} className="flex-shrink-0 px-3 py-2 border border-gray-300 rounded-lg text-xs hover:bg-gray-50">
                {copied ? 'คัดลอกแล้ว ✓' : 'คัดลอก'}
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">คำสั่งจะหาไฟล์ .msi ที่ดาวน์โหลดไว้ให้เอง (ไม่ว่าจะโหลดจาก account ไหนบนเครื่องนั้น) ติดตั้งเงียบ แล้วสั่งให้ agent สแกนและส่งเข้า GLPI ทันที 1 ครั้ง (ไม่ต้องรอ service) จากนั้น service จะสแกนซ้ำให้อัตโนมัติทุกวัน — เช็คได้ด้วย <code>Get-Service glpi-agent</code></p>
            {!glpiUrl && <p className="text-[11px] text-amber-600 mt-1">ยังไม่ได้ตั้งค่า GLPI URL ด้านบน — ใส่ URL ก่อนเพื่อให้คำสั่งนี้ถูกต้อง</p>}
          </div>
        </div>
      )}
    </div>
  )
}

interface GlpiComputerRow {
  id: string; name: string; serial: string | null; manufacturer: string | null; model: string | null
  lastInventoryUpdate: string | null
  matchedAsset: { id: string; serialNumber: string | null; name: string; assetCode: string | null } | null
}
interface GlpiComputerDetail {
  glpiId: string; assetCode: string; serialNumber: string | null; brand: string | null; model: string | null
  os: string | null; cpu: string | null; ram: string | null; hdd: string | null; vga: string | null
  macEthernet: string | null; macWifi: string | null; otherPrograms: string | null; softwareCount: number
}

const GLPI_PREVIEW_FIELDS: { key: keyof GlpiComputerDetail; label: string }[] = [
  { key: 'assetCode',    label: 'Asset Code (Computer Name)' },
  { key: 'brand',        label: 'ยี่ห้อ (Manufacturer)' },
  { key: 'model',        label: 'รุ่น (Model)' },
  { key: 'serialNumber', label: 'Serial Number' },
  { key: 'os',           label: 'ระบบปฏิบัติการ (OS)' },
  { key: 'cpu',          label: 'CPU' },
  { key: 'ram',          label: 'RAM' },
  { key: 'hdd',          label: 'Storage' },
  { key: 'vga',          label: 'การ์ดจอ (VGA)' },
  { key: 'macEthernet',  label: 'Ethernet MAC' },
  { key: 'macWifi',      label: 'WiFi MAC' },
]

function GlpiImport({ companies }: { companies: CompanyItem[] }) {
  const navigate = useNavigate()
  const [configured, setConfigured] = useState(false)
  const [glpiUrl, setGlpiUrl] = useState('')
  const [step, setStep] = useState<'pick' | 'preview' | 'done'>('pick')
  const [search, setSearch] = useState('')
  const [computers, setComputers] = useState<GlpiComputerRow[]>([])
  const [loadingList, setLoadingList] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const [picked, setPicked] = useState<GlpiComputerRow | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [edited, setEdited] = useState<Partial<GlpiComputerDetail>>({})
  const [otherPrograms, setOtherPrograms] = useState<string | null>(null)
  const [softwareCount, setSoftwareCount] = useState(0)
  const [assetType, setAssetType] = useState('LAPTOP')
  const [selectedCompany, setSelectedCompany] = useState('')
  const [result, setResult] = useState<{ ok: boolean; assetId?: string; error?: string } | null>(null)
  const [importing, setImporting] = useState(false)
  const [backfilling, setBackfilling] = useState(false)
  const [backfillMsg, setBackfillMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const { data: assetTypes = [] } = useQuery<{ id: string; label: string; value: string }[]>({
    queryKey: ['masterdata', 'asset-types'],
    queryFn: () => api.get('/masterdata/asset-types').then(r => r.data).catch(() => []),
  })

  const loadComputers = () => {
    if (!configured) return
    setLoadingList(true); setListError(null)
    api.get<GlpiComputerRow[]>('/glpi/computers')
      .then(r => setComputers(r.data))
      .catch(e => setListError(e.response?.data?.message || 'ดึงรายการเครื่องจาก GLPI ไม่สำเร็จ'))
      .finally(() => setLoadingList(false))
  }
  useEffect(loadComputers, [configured])

  const pick = async (row: GlpiComputerRow) => {
    setPicked(row); setLoadingDetail(true)
    try {
      const r = await api.get<GlpiComputerDetail>(`/glpi/computers/${row.id}`)
      setEdited(r.data)
      setOtherPrograms(r.data.otherPrograms)
      setSoftwareCount(r.data.softwareCount)
      setStep('preview')
    } catch (e: any) {
      setListError(e.response?.data?.message || 'ดึงรายละเอียดเครื่องไม่สำเร็จ')
    } finally { setLoadingDetail(false) }
  }

  const doImport = async () => {
    setImporting(true)
    try {
      const assetData: any = {
        assetCode: edited.assetCode, name: edited.assetCode || 'Unknown',
        brand: edited.brand, model: edited.model, serialNumber: edited.serialNumber,
        os: edited.os, cpu: edited.cpu, ram: edited.ram, hdd: edited.hdd, vga: edited.vga,
        macEthernet: edited.macEthernet, macWifi: edited.macWifi,
        glpiId: edited.glpiId,
        otherPrograms,
        ...(!picked?.matchedAsset && { type: assetType, status: 'IN_USE' }),
        ...(!picked?.matchedAsset && selectedCompany && { companyId: selectedCompany }),
      }
      const res = await api.post('/assets/import/glpi', { assetId: picked?.matchedAsset?.id, data: assetData })
      setResult({ ok: true, assetId: res.data.id })
    } catch (e: any) {
      setResult({ ok: false, error: e.response?.data?.message || e.message })
    } finally { setImporting(false); setStep('done') }
  }

  const filtered = computers.filter(c =>
    !search.trim() || [c.name, c.serial, c.manufacturer, c.model].some(v => v?.toLowerCase().includes(search.toLowerCase())))

  const doBackfill = async () => {
    setBackfilling(true); setBackfillMsg(null)
    try {
      const r = await api.post('/glpi/backfill-ids')
      setBackfillMsg({ ok: true, text: r.data.message })
    } catch (e: any) {
      setBackfillMsg({ ok: false, text: e.response?.data?.message || 'อัปเดตไม่สำเร็จ' })
    } finally { setBackfilling(false) }
  }

  return (
    <div className="space-y-4">
      <GlpiConfigPanel onConfigured={setConfigured} onUrlChange={setGlpiUrl} />
      {step === 'pick' && configured && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3 flex-wrap">
          <Link2 size={15} className="text-gray-500" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium text-gray-800">เชื่อม GLPI ID ให้ Asset ที่เคย Sync ไว้ก่อนหน้านี้</p>
            <p className="text-xs text-gray-400 mt-0.5">สำหรับ Asset ที่ sync จาก GLPI มาก่อนที่ระบบจะเก็บ GLPI ID — จะจับคู่ด้วย Serial Number เพื่อให้ปุ่ม "เปิดใน GLPI" ที่หน้า Asset ใช้งานได้</p>
          </div>
          <button onClick={doBackfill} disabled={backfilling}
            className="flex items-center gap-1.5 text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg disabled:opacity-50">
            {backfilling ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} เชื่อม GLPI ID
          </button>
          {backfillMsg && <div className={`w-full text-xs rounded-lg px-3 py-2 ${backfillMsg.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{backfillMsg.text}</div>}
        </div>
      )}
      {step === 'pick' && <AgentDownloadPanel glpiUrl={glpiUrl} />}

      {step === 'pick' && (
        <div className="space-y-4">
          {!configured && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              ตั้งค่าการเชื่อมต่อ GLPI ด้านบนก่อน จึงจะดึงรายการเครื่องได้
            </div>
          )}
          {configured && (
            <>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="ค้นหาชื่อเครื่อง / serial / ยี่ห้อ"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <button onClick={loadComputers} disabled={loadingList}
                  className="flex items-center gap-1.5 text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg">
                  {loadingList ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} รีเฟรช
                </button>
              </div>
              {listError && <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{listError}</div>}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase">ชื่อเครื่อง</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase">Serial</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase">ยี่ห้อ/รุ่น</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase">สถานะ</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filtered.map(c => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-900">{c.name}</td>
                          <td className="px-3 py-2 font-mono text-gray-500">{c.serial || '-'}</td>
                          <td className="px-3 py-2 text-gray-500">{[c.manufacturer, c.model].filter(Boolean).join(' / ') || '-'}</td>
                          <td className="px-3 py-2">
                            {c.matchedAsset
                              ? <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">จะอัปเดต: {c.matchedAsset.assetCode || c.matchedAsset.name}</span>
                              : <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded-full">จะสร้างใหม่</span>}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => pick(c)} disabled={loadingDetail}
                              className="text-blue-600 hover:text-blue-800 font-medium">เลือก →</button>
                          </td>
                        </tr>
                      ))}
                      {!loadingList && filtered.length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">ไม่พบเครื่องใน GLPI</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
            <Server size={16} className="text-gray-500" />
            <span className="text-sm text-gray-700 font-medium">{picked?.name}</span>
            <button onClick={() => setStep('pick')} className="ml-auto text-xs text-gray-400 hover:text-red-500">เปลี่ยนเครื่อง</button>
          </div>
          <div className={`border rounded-xl p-3 text-sm ${picked?.matchedAsset ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
            {picked?.matchedAsset
              ? `จะอัปเดตข้อมูลลง Asset เดิม: ${picked.matchedAsset.assetCode || picked.matchedAsset.name} (จับคู่ด้วย Serial Number)`
              : 'ไม่พบ Asset เดิมที่ Serial ตรงกัน — จะสร้าง Asset ใหม่'}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {GLPI_PREVIEW_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                <input type="text" value={(edited as any)[key] || ''}
                  onChange={e => setEdited(prev => ({ ...prev, [key]: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ))}
            {!picked?.matchedAsset && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">ประเภท Asset</label>
                <select value={assetType} onChange={e => setAssetType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {assetTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">ซอฟต์แวร์ที่ลงไว้ ({softwareCount} รายการ)</label>
            <textarea value={otherPrograms || ''} onChange={e => setOtherPrograms(e.target.value)} rows={6}
              className="w-full px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {!picked?.matchedAsset && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
              <label className="text-sm font-medium text-blue-800 whitespace-nowrap">บริษัท</label>
              <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">— ไม่ระบุ —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => setStep('pick')} className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm hover:bg-gray-50">← กลับ</button>
            <button onClick={doImport} disabled={!edited.assetCode || importing}
              className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {importing ? 'กำลัง Sync...' : picked?.matchedAsset ? 'อัปเดต Asset จาก GLPI' : 'สร้าง Asset จาก GLPI'}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="text-center py-12 space-y-4">
          {result?.ok ? (
            <>
              <CheckCircle size={48} className="mx-auto text-green-500" />
              <h2 className="text-xl font-bold text-gray-900">Sync สำเร็จ!</h2>
              <div className="flex gap-3 justify-center">
                <button onClick={() => { setStep('pick'); setPicked(null); setEdited({}) }}
                  className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm">Sync เครื่องอื่น</button>
                {result.assetId && <button onClick={() => navigate(`/assets/${result.assetId}`)}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">ดู Asset →</button>}
              </div>
            </>
          ) : (
            <>
              <XCircle size={48} className="mx-auto text-red-500" />
              <h2 className="text-xl font-bold text-gray-900">Sync ไม่สำเร็จ</h2>
              <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{result?.error}</p>
              <button onClick={() => setStep('preview')} className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm">← กลับแก้ไข</button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main ImportPage ──────────────────────────────────────────────────────────

export default function ImportPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'csv' | 'winaudit' | 'glpi'>('csv')

  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload')
  const [rows, setRows] = useState<MappedRow[]>([])
  const [fileName, setFileName] = useState('')
  const [results, setResults] = useState<{ ok: number; fail: number; errors: string[] }>({ ok: 0, fail: 0, errors: [] })
  const [selectedCompany, setSelectedCompany] = useState<string>('')
  const [selectedSite, setSelectedSite] = useState<string>('')
  const [showBuilder, setShowBuilder] = useState(false)

  const { data: companies = [] } = useQuery<CompanyItem[]>({
    queryKey: ['companies'],
    queryFn: () => api.get('/masterdata/companies').then(r => r.data)
  })
  const { data: assetTypes = [] } = useQuery<MasterOption[]>({ queryKey: ['masterdata', 'asset-types'], queryFn: () => api.get('/masterdata/asset-types').then(r => r.data) })
  const { data: assetStatuses = [] } = useQuery<MasterOption[]>({ queryKey: ['masterdata', 'asset-statuses'], queryFn: () => api.get('/masterdata/asset-statuses').then(r => r.data) })
  const known: KnownOptions = { types: assetTypes, statuses: assetStatuses }
  const { data: allSites = [] } = useQuery<{ id: string; name: string; companyId?: string; company?: { id: string } }[]>({ queryKey: ['masterdata', 'sites'], queryFn: () => api.get('/masterdata/sites').then(r => r.data).catch(() => []) })
  const { data: allDepts = [] } = useQuery<{ id: string; name: string; siteId?: string; site?: { id: string } }[]>({ queryKey: ['masterdata', 'departments'], queryFn: () => api.get('/masterdata/departments').then(r => r.data).catch(() => []) })
  const companySites = allSites.filter(s => s.companyId === selectedCompany || s.company?.id === selectedCompany)
  const siteDepts = allDepts.filter(d => d.siteId === selectedSite || d.site?.id === selectedSite)
  const optionLabel = (opts: MasterOption[], v: string) => opts.find(o => o.value === v)?.label || v

  const onDrop = useCallback((files: File[]) => {
    if (!files.length) return
    const file = files[0]
    setFileName(file.name)
    Papa.parse(file, {
      header: true, skipEmptyLines: true, encoding: 'UTF-8',
      complete: (result) => {
        const mapped = (result.data as ParsedRow[]).map((r, i) => mapRow(r, i + 2, known))
        setRows(mapped)
        setStep('preview')
      }
    })
  }, [assetTypes, assetStatuses])

  const applyBuilderRows = (built: ParsedRow[]) => {
    setFileName('ข้อมูลจากตัวสร้าง Template')
    setRows(built.map((r, i) => mapRow(r, i + 1, known)))
    setShowBuilder(false)
    setStep('preview')
  }
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv', 'xls'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
    maxFiles: 1
  })

  const importMutation = useMutation({
    mutationFn: async () => {
      const valid = rows.filter(r => r._errors.length === 0)
      let ok = 0; const errors: string[] = []
      for (const row of valid) {
        const { _rowIndex, _errors, ...data } = row
        const company = companies.find(c => c.id === selectedCompany)
        const site = companySites.find(s => s.id === selectedSite)
        // Link the department text to the site's Department record when the name matches
        const dept = site && data.department ? siteDepts.find(d => d.name.trim().toLowerCase() === String(data.department).toLowerCase()) : undefined
        try {
          await api.post('/assets', {
            ...data,
            type: data.type || 'LAPTOP',
            status: data.status || 'AVAILABLE',
            dataSource: 'CSV',
            ...(company && { companyId: company.id, company: company.name }),
            ...(site && { siteId: site.id, site: site.name }),
            ...(dept && { departmentId: dept.id }),
          })
          ok++
        } catch (e: any) { errors.push(`แถว ${_rowIndex}: ${e.response?.data?.message || e.message}`) }
      }
      return { ok, fail: valid.length - ok, errors }
    },
    onSuccess: (r) => { setResults(r); setStep('done') }
  })

  const downloadTemplate = () => {
    const csv = [TEMPLATE_HEADERS.join(','), TEMPLATE_EXAMPLE.join(',')].join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = 'asset-import-template.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const previewFields = PREVIEW_FIELDS.filter(([k]) => rows.some(r => r[k] != null && r[k] !== ''))
  const formatCell = (key: string, v: any) => {
    if (v == null || v === '') return '-'
    if (key === 'type') return optionLabel(assetTypes, v)
    if (key === 'status') return optionLabel(assetStatuses, v)
    if (['purchaseDate', 'warrantyExpiry', 'contractEnd'].includes(key)) return String(v).slice(0, 10)
    if (['purchasePrice', 'depreciation', 'netBook'].includes(key)) return Number(v).toLocaleString()
    return String(v)
  }

  const validCount = rows.filter(r => r._errors.length === 0).length
  const errorCount = rows.filter(r => r._errors.length > 0).length

  return (
    <div className={`p-6 mx-auto space-y-6 ${activeTab === 'csv' && step === 'preview' ? 'max-w-7xl' : 'max-w-4xl'}`}>
      <h1 className="text-xl font-bold text-gray-900">นำเข้า Assets</h1>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button onClick={() => setActiveTab('csv')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'csv' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
          <FileText size={15} /> นำเข้าจาก CSV
        </button>
        <button onClick={() => setActiveTab('winaudit')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'winaudit' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
          <Monitor size={15} /> นำเข้าจาก WinAudit
        </button>
        <button onClick={() => setActiveTab('glpi')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'glpi' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
          <Server size={15} /> Sync จาก GLPI
        </button>
      </div>

      {activeTab === 'winaudit' && <WinAuditImport companies={companies} />}
      {activeTab === 'glpi' && <GlpiImport companies={companies} />}

      {showBuilder && <AssetTemplateBuilder onClose={() => setShowBuilder(false)} onUseRows={applyBuilderRows} />}

      {activeTab === 'csv' && (
        <>
          <div className="flex items-center gap-2 text-sm">
            {[['upload', '1. อัปโหลดไฟล์'], ['preview', '2. ตรวจสอบ'], ['done', '3. เสร็จสิ้น']].map(([s, l], i) => (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <ChevronRight size={14} className="text-gray-300" />}
                <span className={`px-3 py-1.5 rounded-full font-medium ${step === s ? 'bg-blue-600 text-white' : ['upload', 'preview', 'done'].indexOf(s) <= ['upload', 'preview', 'done'].indexOf(step) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>{l}</span>
              </div>
            ))}
          </div>

          {step === 'upload' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">วิธีใช้งาน</p>
                  <ul className="text-sm text-amber-700 mt-1 space-y-1 list-disc list-inside">
                    <li>กด <strong>สร้าง Template / กรอกข้อมูล</strong> เพื่อเลือกคอลัมน์และกรอกข้อมูลในหน้านี้ แล้วดาวน์โหลดเป็น CSV หรือนำเข้าได้ทันที</li>
                    <li>หรือดาวน์โหลด Template เปล่า กรอกใน Excel/Google Sheets แล้ว Export เป็น CSV (UTF-8) มาอัปโหลดที่นี่</li>
                    <li>ชื่อคอลัมน์รองรับทั้งภาษาไทยและอังกฤษ</li>
                    <li>คอลัมน์ที่ต้องมี: <strong>ASSET_CODE</strong> (ถ้าไม่ระบุชื่อ จะใช้ Asset Code เป็นชื่อ)</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setShowBuilder(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors">
                  <Table2 size={16} /> สร้าง Template / กรอกข้อมูล
                </button>
                <button onClick={downloadTemplate} className="flex items-center gap-2 px-4 py-2.5 border border-blue-300 text-blue-700 rounded-xl text-sm hover:bg-blue-50 transition-colors">
                  <Download size={16} /> ดาวน์โหลด Template เปล่า
                </button>
              </div>
              <div {...getRootProps()} className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}>
                <input {...getInputProps()} />
                <Upload size={32} className="mx-auto text-gray-300 mb-3" />
                <p className="font-medium text-gray-600">{isDragActive ? 'วางไฟล์ที่นี่...' : 'Drag & drop หรือคลิกเลือกไฟล์'}</p>
                <p className="text-sm text-gray-400 mt-1">รองรับ .csv (UTF-8)</p>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-sm"><FileText size={14} />{fileName}</div>
                <div className="flex gap-3">
                  <span className="flex items-center gap-1.5 text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded-lg"><CheckCircle size={14} /> {validCount} แถวพร้อม import</span>
                  {errorCount > 0 && <span className="flex items-center gap-1.5 text-sm text-red-700 bg-red-50 px-3 py-1.5 rounded-lg"><XCircle size={14} /> {errorCount} แถวมีปัญหา</span>}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex items-center gap-3">
                    <span className="text-sm font-medium text-blue-800 whitespace-nowrap w-28">บริษัท (Company)</span>
                    <select value={selectedCompany} onChange={e => { setSelectedCompany(e.target.value); setSelectedSite('') }}
                      className="flex-1 min-w-0 px-3 py-2 text-sm border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">— ไม่ระบุ —</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-3">
                    <span className="text-sm font-medium text-blue-800 whitespace-nowrap w-28">Site</span>
                    <select value={selectedSite} onChange={e => setSelectedSite(e.target.value)} disabled={!selectedCompany}
                      className="flex-1 min-w-0 px-3 py-2 text-sm border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400">
                      <option value="">{selectedCompany ? (companySites.length ? '— ไม่ระบุ —' : '— บริษัทนี้ยังไม่มี Site —') : '— เลือกบริษัทก่อน —'}</option>
                      {companySites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </label>
                </div>
                <p className="text-xs text-blue-600">จะ apply ให้ทุก asset ที่ import{selectedSite && previewFields.some(([k]) => k === 'department') ? ' · แผนกในไฟล์ที่ชื่อตรงกับแผนกของ Site นี้จะถูกผูกให้อัตโนมัติ' : ''}</p>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase">#</th>
                        <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase">ตรวจ</th>
                        {previewFields.map(([k, label]) => (
                          <th key={k} className="px-3 py-2 text-left text-gray-500 font-semibold uppercase whitespace-nowrap">{label}</th>
                        ))}
                        {errorCount > 0 && <th className="px-3 py-2 text-left text-gray-500 font-semibold uppercase">ปัญหา</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {rows.map((row, i) => (
                        <tr key={i} className={row._errors.length ? 'bg-red-50' : 'hover:bg-gray-50'}>
                          <td className="px-3 py-2 text-gray-400">{row._rowIndex}</td>
                          <td className="px-3 py-2">{row._errors.length ? <XCircle size={14} className="text-red-500" /> : <CheckCircle size={14} className="text-green-500" />}</td>
                          {previewFields.map(([k]) => (
                            <td key={k} className={`px-3 py-2 whitespace-nowrap max-w-xs truncate ${k === 'assetCode' || k === 'name' ? 'font-medium text-gray-900' : 'text-gray-600'} ${MONO_FIELDS.has(k) ? 'font-mono' : ''}`} title={row[k] != null ? String(row[k]) : ''}>
                              {formatCell(k, row[k])}
                            </td>
                          ))}
                          {errorCount > 0 && <td className="px-3 py-2 text-red-600 whitespace-nowrap">{row._errors.join(', ')}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep('upload')} className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm hover:bg-gray-50">← กลับ</button>
                <button onClick={() => importMutation.mutate()} disabled={validCount === 0 || importMutation.isPending}
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {importMutation.isPending ? `กำลัง import ${validCount} assets...` : `Import ${validCount} Assets`}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-12 space-y-4">
              <CheckCircle size={48} className="mx-auto text-green-500" />
              <h2 className="text-xl font-bold text-gray-900">Import เสร็จสิ้น!</h2>
              <div className="flex justify-center gap-4">
                <div className="bg-green-50 px-6 py-3 rounded-xl"><p className="text-2xl font-bold text-green-700">{results.ok}</p><p className="text-sm text-green-600">นำเข้าสำเร็จ</p></div>
                {results.fail > 0 && <div className="bg-red-50 px-6 py-3 rounded-xl"><p className="text-2xl font-bold text-red-700">{results.fail}</p><p className="text-sm text-red-600">ล้มเหลว</p></div>}
              </div>
              {results.errors.length > 0 && (
                <div className="bg-red-50 rounded-xl p-4 text-left max-w-lg mx-auto">
                  <p className="text-sm font-medium text-red-800 mb-2">Errors:</p>
                  {results.errors.map((e, i) => <p key={i} className="text-xs text-red-700">{e}</p>)}
                </div>
              )}
              <div className="flex gap-3 justify-center">
                <button onClick={() => { setStep('upload'); setRows([]); setFileName('') }} className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm">Import ไฟล์อื่น</button>
                <button onClick={() => navigate('/assets')} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">ดู Assets ทั้งหมด →</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
